import React, { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import qs from 'query-string';
import { useQuery, useInfiniteQuery } from "react-query";

import { MainContainer, MainTitle, TitleContainer, ButtonsRow, MainButton, Divider, HeadSeo, Paragraph, FiltersPopup, Breadcrumbs, GoogleAnalytics, ColumnContainer, Column } from '@beacharound/beacharound-ui';

import { sandTypes } from '@beacharound/beacharound-ui-helpers';

import ActivitiesList from '../../components/Activities/ActivitiesList';
import BeachesSlider from '../../components/Beaches/BeachesSlider';

import { getBeaches } from '../../api/beach';
import { getPlace } from '../../api/places';
import { getServices } from '../../api/services';
import { getAccessibilities } from '../../api/accessibilities';
import { getPlaceholderImage } from '../../helpers/images/getPlaceholderImage';
import { getGeoBreadCrumbs } from '../../helpers/getGeoBreadCrumbs';
import { AdSense } from '../../components/AdSense/AdSense';

export default function CityBeaches({ match, location }) {

    const isPublic = match.url.indexOf('/spiagge-libere') > 0;
    const queryParams = qs.parse(location.search);
    const { placeSlug } = match.params;
    const [filtersPopup, setFiltersPopup] = useState();
    const [filtersData, setFiltersData] = useState();

    const elementLimit = 40;

    let beachesOptions = {
        ...queryParams,
        place: placeSlug,
        limit: elementLimit,
        maxDistance: true,
        params: { type: isPublic ? "public" : "private" }
    }

    if (filtersData?.services) {
        beachesOptions = {
            ...beachesOptions,
            services: filtersData.services
        }
    }

    if (filtersData?.accessibilities) {
        beachesOptions = {
            ...beachesOptions,
            accessibilities: filtersData.accessibilities
        }
    }

    if (filtersData?.sandType) {
        beachesOptions["params"] = {
            ...beachesOptions["params"],
            sandType: { $in: filtersData.sandType }
        }
    }

    // SPIAGGE

    const {
        data: infiniteData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery(
        ["beaches", beachesOptions],
        ({ pageParam = 0 }) => {
            return getBeaches({ params: { ...beachesOptions, skip: pageParam } })
        },
        {
            getNextPageParam: (lastPage, pages) => {
                if (lastPage.length < elementLimit) { return }
                return lastPage.length > 0 && elementLimit * pages.length;
            }
        }
    )

    const beaches = [...(infiniteData?.pages?.reduce((acc, page) => page && Array.isArray(page) ? [...acc, ...page] : acc, []) || [])];

    // SERVIZI

    const { data: services } = useQuery(["services", "beaches"], () =>
        getServices({ params: { type: "beaches" }, limit: 1000, sort: { name: 1 } })
    );
    // ACCESSIBILITÀ
    const { data: accessibilities } = useQuery(["accessibilities", "beaches"], () =>
        getAccessibilities({ params: { type: "beaches" } })
    )
    // LUOGO
    const { data: place } = useQuery(["places", placeSlug], () =>
        getPlace({ slug: placeSlug })
    )
    const placeName = place?.name;

    const data = beaches?.map((beach) => {
        return {
            ...beach,
            cover: beach.cover?.length > 0 ? beach.cover : getPlaceholderImage('beaches', beach),
            description: undefined,
            component: Link,
            to: `/spiagge/${beach.slugCity || beach.slug}`
        }
    })

    const filters = [{
        type: 'checkbox',
        title: 'Servizi',
        name: "services",
        list: services?.map((s) => {
            return { name: s.name, slug: s.slug }
        })
    }, {
        type: 'checkbox',
        title: 'Accessibilità',
        name: "accessibilities",
        list: accessibilities?.map((s) => {
            return { name: s.name, slug: s.slug }
        })
    }, {
        type: 'checkbox',
        title: 'Tipologia sabbia',
        name: "sandType",
        list: sandTypes.map((type) => {
            return { name: type.label, slug: type.value }
        })
    }]

    function filterHandler(data) {
        return setFiltersData(data);
    }

    const first = data?.slice(0, 4)
    const last = data?.splice(4)

    return (
        <Fragment>
            <HeadSeo
                title={[`Prenota le spiagge a ${placeName}`]}
                description={`Prenota l'ombrellone a ${placeName}. Scegli tra sdraio o lettino, stabilimento balneare o lido al miglior prezzo online`}
                image={place?.cover}
            />

            {place?.name && (
                <GoogleAnalytics name={[place?.name, "Spiagge"]} type={place?.type} />
            )}

            <MainContainer>
                <TitleContainer>
                    <Breadcrumbs
                        breadcrumbs={getGeoBreadCrumbs(place?.geoElements, {
                            others: [{
                                label: placeName,
                                url: match.url.replace('/spiagge', '')
                            }, {
                                label: "Spiagge",
                                url: match.url
                            }]
                        })}
                        linkComponent={Link}
                    />
                    <MainTitle
                        text={`Prenota le spiagge a <strong>${placeName}</strong>`}
                        component="h1"
                    />
                    <Paragraph
                        text={`Prenota le spiagge a ${placeName}. Stabilimento o Lido? Ombrellone, sdraio e lettino al miglior prezzo`}
                        component="h2"
                    />
                </TitleContainer>

                <ButtonsRow marginBottom>
                    {/* <MainButton border text="Ordina" action={() => setOrderPopup(true)}/> */}
                    <MainButton
                        border
                        text="Filtra"
                        notifications={filtersData &&
                            Object.keys(filtersData).reduce(
                                (accumulator, name) => {
                                    return (accumulator = [
                                        ...accumulator,
                                        ...filtersData[name],
                                    ]);
                                },
                                []
                            ).length
                        }
                        action={() => setFiltersPopup(true)}
                    />
                </ButtonsRow>

                <FiltersPopup
                    filtersList={filters}
                    data={filtersData}
                    title="Filtra le spiagge"
                    visible={filtersPopup}
                    setVisible={() => setFiltersPopup(false)}
                    filterHandler={filterHandler}
                />
            </MainContainer>

            <ActivitiesList
                type="beaches"
                data={first}
                city={place?.slug}
                marginBottom
            />

            <BeachesSlider
                title={`Spiaggie consigliate a ${placeName}`}
                place={placeSlug}
                highlight
                darkBg
                noMargin
            />

            <MainContainer>
                <ColumnContainer noMargin>
                    <Column>
                        <AdSense
                            client="ca-pub-7021525903157736"
                            slot="7423982181"
                            format='autorelaxed'
                            contentUiType="image_stacked,image_stacked"
                            contentColumns="1,2"
                            contentRows="4,2"
                        />
                    </Column>
                </ColumnContainer>
            </MainContainer>

            {last?.length > 0 &&
                <ActivitiesList
                    type="beaches"
                    data={last}
                    city={place?.slug}
                    fetchMore={fetchNextPage}
                    hasMore={hasNextPage}
                    isLoadingMore={isFetchingNextPage}
                />
            }

            <MainContainer>
                <Divider />
                <TitleContainer>
                    <Paragraph
                        text={`Cosa scegliere: ombrellone, sdraio o lettino? stabilimento balneare o lido? l'importante è avere un posto riservato al miglior prezzo online! Prenota subito le spiagge a ${placeName}. Basta code e stressanti attese sotto il sole ma solo pace e relax.  Sdraio, lettino e ombrellone, insieme a tutto ciò che ti serve sono già in stabilimento ad attenderti. Se preferisci puoi vivere il tuo meritato riposso anche nella pace di una spiaggia libera. Non ti dovrai più preoccupare di nulla e potrai dedicarti unicamente a divertirti e rilassarti. Dai un occhio qui di seguito e scopri qual'è il luogo più adatto alle tue esigenze; a tutto il resto ci pensa Beacharound!`}
                    />
                </TitleContainer>
            </MainContainer>
        </Fragment>
    );
}